import { useMemo } from 'react'
import { Outlet, useLocation, useOutletContext } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'

import {
  fetchMosquitoObservationsByProps, fetchSightingsByMission, fetchUniqueSpeciesByMission,
} from '@services/data.service'

import MosquitoObservationsSightingsMapData from './MosquitoObservationsSightingsMapLogic/MosquitoObservationsSightingsMapLogic'
import MosquitoObservationsDetectionMapLogic from './MosquitoObservationsDetectionMapLogic/MosquitoObservationsDetectionMapLogic'

import './mosquito-observations.styl'

function MosquitoObservations() {
  const {
    sourcesActiveIds = [],
    mosquitoTypeInactiveTypeIds = [],
    mosquitoSexActiveIds = [],
    smartTrapFilterIds = [],
    selectionRange,
  } = useSelector(state => state.mosquitoDetection)
  const { activeProject } = useSelector(state => state.missionSetup)

  const location = useLocation()
  const { map } = useOutletContext()
  const isTrapsRoute = location.pathname.includes('/mosquito-observations/detections')

  const { data: uniqueSpecies, isLoading: uniqueSpeciesLoading } = useQuery({
    queryKey: ['uniqueSpecies', activeProject?.id, selectionRange, sourcesActiveIds, mosquitoSexActiveIds, smartTrapFilterIds],
    queryFn: () => fetchUniqueSpeciesByMission(
      activeProject?.id,
      selectionRange?.startDate,
      selectionRange?.endDate,
      sourcesActiveIds,
      mosquitoSexActiveIds,
      smartTrapFilterIds,
    ),
    enabled: !!activeProject?.id,
  })

  const mosquitoSpecies = useMemo(() => {
    if (!uniqueSpecies) return []
    return uniqueSpecies.map(({ name, color, count }) => ({
      id: name, label: name, group: 'other', icon: 'mosquito', color, amount: count,
    }))
  }, [uniqueSpecies])

  const mosquitoTypeActiveTypeIds = useMemo(() => {
    if (!mosquitoSpecies.length) return []
    const allTypeIds = mosquitoSpecies.map(({ id }) => id)
    return allTypeIds.filter(id => !mosquitoTypeInactiveTypeIds.includes(id))
  }, [mosquitoSpecies, mosquitoTypeInactiveTypeIds])

  const mosquitoSpeciesColorsByName = useMemo(() => {
    return new Map(mosquitoSpecies.map(species => [
      String(species.id || '').toLowerCase(),
      species.color,
    ]))
  }, [mosquitoSpecies])

  const { data: sightings, isLoading: isSightingsLoading } = useQuery({
    queryKey: [
      'sightings', activeProject?.id, selectionRange, sourcesActiveIds,
      mosquitoSexActiveIds, mosquitoTypeActiveTypeIds,
    ],
    queryFn: () => fetchSightingsByMission(
      activeProject?.id, selectionRange.startDate, selectionRange.endDate, sourcesActiveIds,
      mosquitoSexActiveIds, mosquitoTypeActiveTypeIds,
    ),
    enabled: !!activeProject?.id,
  })

  const { data: observations, isLoading: isObservationsLoading } = useQuery({
    queryKey: [
      'observations', activeProject?.id, selectionRange, sourcesActiveIds,
      mosquitoTypeInactiveTypeIds, mosquitoSexActiveIds, smartTrapFilterIds,
    ],
    queryFn: () => fetchMosquitoObservationsByProps(
      activeProject?.id, sourcesActiveIds, mosquitoTypeInactiveTypeIds,
      selectionRange.startDate, selectionRange.endDate, mosquitoSexActiveIds, smartTrapFilterIds,
    ),
    enabled: !!activeProject?.id,
  })

  const sightingsWithSpeciesColor = useMemo(() => {
    if (!sightings) return []
    return sightings.map((sighting) => {
      const sightingName = String(sighting.properties?.identification?.name || 'unclassified').toLowerCase()
      return {
        ...sighting,
        properties: {
          ...sighting.properties,
          identification: {
            ...sighting.properties?.identification,
            color: mosquitoSpeciesColorsByName.get(sightingName),
          },
        },
      }
    })
  }, [sightings, mosquitoSpeciesColorsByName])

  return (
    <div className="mosquito-observations">
      <Outlet
        context={{
          map,
          mosquitoSpecies,
          sightings: sightingsWithSpeciesColor,
          observations,
          isLoading: isSightingsLoading || isObservationsLoading || uniqueSpeciesLoading,
        }}
      />
      {isTrapsRoute && (
        <MosquitoObservationsDetectionMapLogic
          map={map}
          observations={observations}
        />
      )}
      {!isTrapsRoute && (
        <MosquitoObservationsSightingsMapData
          map={map}
          sightings={sightingsWithSpeciesColor}
        />
      )}
    </div>
  )
}

export default MosquitoObservations
